import { useMemo } from "react";
import { useRouter, useLocation, Link } from "@tanstack/react-router";

import { ICLogo } from "@/components/Icon/ICLogo";
import { Button } from "@mui/material";
import Language from "@/components/Layout/Navbar/language";
import config from "@/config";
import { usePrivy } from "@privy-io/react-auth";
import { useAppSelector } from "@/redux/hooks";
import CardInfo from "@/components/Card";
import LoginButton from "@/components/Layout/Navbar/LoginButton/LoginButton";
import UserInfo from "@/components/Layout/Navbar/UserInfo/UserInfo";
import MobileNavigation from "@/components/Layout/Navbar/Navigation/Mobile";
import DesktopNavigation from "@/components/Layout/Navbar/Navigation/Desktop";
import { useTranslation } from "react-i18next";
import NetworkSituation from "@/components/Layout/Navbar/NetworkSituation";

export default function Navigation() {
  const router = useRouter();
  const privyState = usePrivy();
  const googleAccount = privyState.user?.google;
  const isVerified = useAppSelector((state) => state.verify.isVerified);
  const { t } = useTranslation();
  const privyToken = localStorage.getItem("privy:token");
  const { authenticated } = useMemo(
    () => ({
      authenticated: privyState.authenticated,
    }),
    [privyState.ready, privyState.authenticated],
  );

  const location = useLocation();
  const isVerifyPath = location.pathname === "/verify";

  function handlePath(path: string) {
    router.navigate({ to: path });
  }

  if (location.pathname === "/") {
    // Render LandingPageNavigation
    return (
      <header className="flex justify-between items-center p-4 bg-transparent md:px-[2vw] xl:px-[8vw]">
        <Link 
          to="/" 
          className={`flex items-center space-x-2 ${isVerifyPath ? 'pointer-events-none opacity-50' : ''}`}
        >
          <ICLogo width="40" height="40" />
          <span className="text-xl font-bold text-white"> MoneyFi </span>
        </Link>
        <aside className="flex space-x-2 items-center justify-center">
          <Language />
          <Button
            variant="contained"
            sx={{ backgroundColor: "var(--brand-primary-green)" }}
            onClick={() => handlePath(config.PATHNAME.VAULT)}
            disabled={isVerifyPath}
          >
            {t("landing_page.header.launch_app")}
          </Button>
        </aside>
      </header>
    );
  }

  // Render Navbar for other routes
  return (
    <header className="flex justify-between items-center p-4 bg-transparent md:px-[2vw] xl:px-[8vw]">
      <Link 
        to="/" 
        className={`flex items-center space-x-2 ${isVerifyPath || location.pathname === "/not-found" ? 'pointer-events-none opacity-50' : ''}`}
      >
        <ICLogo width="40" height="40" />
        <span className="text-xl font-bold text-white"> MoneyFi </span>
        {/* Desktop Navigation (hidden on mobile, visible on sm and above) */}
      </Link>
        <DesktopNavigation />
      <aside className="flex items-center justify-center">
        {privyToken ? <NetworkSituation /> : null}
        <Language />
        <div className="lg:hidden">
          <MobileNavigation />
        </div>

        <div className="hidden lg:flex items-center space-x-2">
          {authenticated ? (
            <>
              {isVerified || !googleAccount ? (
                <CardInfo className={`flex items-center justify-center ${isVerifyPath ? 'pointer-events-none opacity-50' : ''}`}>
                  <UserInfo />
                </CardInfo>
              ) : null}
            </>
          ) : (
            <LoginButton />
          )}
        </div>
      </aside>
    </header>
  );
}
